import { Casino } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material'
import axios from 'axios'
import { Item } from 'chezy-inventory-backend/bin/database/types/Item'
import { useFormik } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'
import * as yup from 'yup'

import { getBaseURL } from '../../utils/getBaseURL'

interface ItemFormData {
  id: string
  name: string
  cost: string | undefined
  image?: File
  qty: number
  instructions?: string
}

const CreateItemForm = (props: {
  isOpen: boolean
  data?: Item
  editing?: boolean | undefined
  onDismiss: () => void
  onSave: (data: ItemFormData) => void
}) => {
  const [openDate, setOpenDate] = useState(Date.now())
  const defaultData = props.data
    ? {
        ...props.data,
        cost: props.data?.cost !== undefined && props.data?.cost !== null ? String(props.data.cost) : undefined,
      }
    : {
        id: '',
        name: '',
        cost: '',
        qty: 1,
        instructions: '',
      }

  defaultData.cost = defaultData.cost ?? ''
  defaultData.id = defaultData.id ?? ''
  defaultData.name = defaultData.name ?? ''
  defaultData.qty = defaultData.qty ?? 1
  defaultData.instructions = defaultData.instructions ?? ''

  useEffect(() => {
    if (props.isOpen) {
      formik.resetForm()
      formik.validateForm()
      formik.setValues(defaultData)
      setSelectedImage(undefined)
      setOpenDate(Date.now())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen])

  const [selectedImage, setSelectedImage] = useState<File | undefined>()

  const imageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedImage(e.target.files[0])
    }
  }

  const validationSchema = yup.object({
    id: yup.string().required('Item id is required'),
    name: yup.string().required('Item name is required'),
    cost: yup.number().positive('Cost cannot be negative').max(999999.99),
    qty: yup.number().positive().required(),
  })

  const formik = useFormik<ItemFormData>({
    initialValues: defaultData,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const data = {
        ...values,
        image: selectedImage,
      }
      props.onSave(data)
    },
  })

  const getID = useCallback(() => {
    axios.get(`${getBaseURL()}/items/makeID`).then((resp) => {
      formik.setFieldValue('id', resp.data as string)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Dialog open={props.isOpen} onClose={props.onDismiss}>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>{props.editing ? `Editing ${props.data?.name}` : 'Add an item'}</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'row', minWidth: 500 }}>
            <div style={{ flex: 2, marginRight: 30 }}>
              {!props.editing && (
                <TextField
                  autoFocus
                  margin="dense"
                  id="id"
                  label="Item ID / Scan Code"
                  type="text"
                  fullWidth
                  name="id"
                  variant="standard"
                  value={formik.values.id}
                  onChange={formik.handleChange}
                  error={formik.touched.id && Boolean(formik.errors.id)}
                  helperText={formik.touched.id && formik.errors.id}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Generate random id">
                          <IconButton onClick={getID}>
                            <Casino />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              <TextField
                autoFocus
                margin="dense"
                id="name"
                label="Item Name"
                type="text"
                fullWidth
                name="name"
                variant="standard"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
              <TextField
                autoFocus
                margin="dense"
                id="cost"
                label="Replacement Cost (Optional)"
                name="cost"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                type="text"
                fullWidth
                variant="standard"
                value={formik.values.cost}
                onChange={formik.handleChange}
                error={formik.touched.cost && Boolean(formik.errors.cost)}
                helperText={formik.touched.cost && formik.errors.cost}
              />
              <TextField
                autoFocus
                margin="dense"
                id="qty"
                label="Quantity of item (if more than one item per ID)"
                name="qty"
                InputProps={{
                  endAdornment: <InputAdornment position="start">count</InputAdornment>,
                }}
                type="text"
                fullWidth
                variant="standard"
                value={formik.values.qty}
                onChange={formik.handleChange}
                error={formik.touched.qty && Boolean(formik.errors.qty)}
                helperText={formik.touched.qty && formik.errors.qty}
              />
              <TextField
                autoFocus
                margin="dense"
                id="qty"
                label="Optional packing instructions"
                name="instructions"
                type="text"
                fullWidth
                variant="standard"
                value={formik.values.instructions}
                onChange={formik.handleChange}
                error={formik.touched.instructions && Boolean(formik.errors.instructions)}
                helperText={formik.touched.instructions && formik.errors.instructions}
              />
            </div>

            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              {(selectedImage || props.editing) && (
                <>
                  <div>Image Preview:</div>
                  <div>
                    <img
                      src={
                        !selectedImage
                          ? `${getBaseURL()}/item/${props.data?.id}/picture?ts=${openDate}`
                          : URL.createObjectURL(selectedImage)
                      }
                      style={{ maxWidth: 100, maxHeight: 100 }}
                      alt={props.data?.name}
                    />
                  </div>
                </>
              )}
              <div>
                <Button variant="contained" component="label">
                  {selectedImage ? 'Change Image' : 'Upload Image'}
                  <input hidden accept="image/*" multiple type="file" name="image" onChange={imageChange} />
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onDismiss} color="error">
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export { CreateItemForm }
export type { ItemFormData }
